import { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Ruler } from "@phosphor-icons/react";
import { formatDateIT } from "@/lib/formatDate";
import { OverviewCard, OverviewEmpty } from "./Card";

/**
 * Widget #2 — Peso & misure.
 * Selettore per scegliere la metrica visualizzata.
 */

const METRICS = [
  { key: "weight_kg", label: "Peso", suffix: " kg", color: "#007AFF" },
  { key: "body_fat_pct", label: "Massa grassa", suffix: " %", color: "#FF9F0A" },
  { key: "chest_cm", label: "Petto", suffix: " cm", color: "#34C759" },
  { key: "waist_cm", label: "Vita", suffix: " cm", color: "#FF3B30" },
  { key: "hips_cm", label: "Fianchi", suffix: " cm", color: "#AF52DE" },
  { key: "biceps_cm", label: "Bicipiti", suffix: " cm", color: "#5856D6" },
  { key: "thighs_cm", label: "Cosce", suffix: " cm", color: "#FF2D55" },
  { key: "calves_cm", label: "Polpacci", suffix: " cm", color: "#30B0C7" },
  { key: "shoulders_cm", label: "Spalle", suffix: " cm", color: "#D4FF00" },
];

function tooltipFormatter(suffix) {
  return ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[#171717] border border-[#262626] rounded-lg p-2.5 text-xs shadow-2xl">
        <p className="text-zinc-400 mb-1">{label}</p>
        {payload.map((entry) => (
          <p key={entry.dataKey} className="font-medium" style={{ color: entry.color }}>
            {entry.name}: <span className="text-white">{entry.value}{suffix}</span>
          </p>
        ))}
      </div>
    );
  };
}

export function MeasurementsChartWidget({ measurements }) {
  // Choose default metric: prefer weight, otherwise first available metric.
  const availableKeys = useMemo(() => {
    const keys = new Set();
    (measurements || []).forEach((m) => {
      METRICS.forEach((meta) => {
        if (m[meta.key] != null) keys.add(meta.key);
      });
    });
    return keys;
  }, [measurements]);

  const initialMetric = availableKeys.has("weight_kg")
    ? "weight_kg"
    : (METRICS.find((m) => availableKeys.has(m.key))?.key || "weight_kg");

  const [metric, setMetric] = useState(initialMetric);
  const meta = METRICS.find((m) => m.key === metric) || METRICS[0];

  const series = useMemo(() => {
    return (measurements || [])
      .filter((m) => m[metric] != null)
      .map((m) => ({
        date: formatDateIT(m.date),
        value: Number(m[metric]),
      }));
  }, [measurements, metric]);

  const optionList = METRICS.filter((m) => availableKeys.has(m.key));

  return (
    <OverviewCard
      title="Peso & misure"
      icon={Ruler}
      iconColor="text-[#FF9F0A]"
      testId="overview-measurements-chart"
      action={
        optionList.length > 1 ? (
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            className="bg-[#171717] border border-[#262626] rounded-md text-xs text-zinc-300 px-2 py-1 outline-none focus:border-[#007AFF]"
            data-testid="overview-measurement-metric"
          >
            {optionList.map((m) => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </select>
        ) : null
      }
    >
      {series.length === 0 ? (
        <OverviewEmpty message="Nessuna misura registrata per questa metrica." />
      ) : (
        <div className="h-[200px] w-full -mx-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
              <XAxis dataKey="date" stroke="#52525B" tick={{ fontSize: 10 }} />
              <YAxis stroke="#52525B" tick={{ fontSize: 10 }} domain={["auto", "auto"]} />
              <Tooltip content={tooltipFormatter(meta.suffix)} />
              <Line
                type="monotone"
                dataKey="value"
                name={meta.label}
                stroke={meta.color}
                strokeWidth={2}
                dot={{ r: 3, fill: meta.color }}
                activeDot={{ r: 5 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </OverviewCard>
  );
}
